import React, { useEffect } from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import MagneticButton from "../components/site/ui/MagneticButton";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { Radio, Cpu, Hexagon, Layers, ArrowUpRight } from "lucide-react";
import { trackEvent } from "../lib/track";

const SMILE_URL = "http://www.TheSmileChannel.com";
const METATRON_URL = "https://metatronconcepts.com";
const RELICTEK_URL = "https://www.relictekindustries.com";

const STATUS_STYLES = {
  active: { label: "Active", className: "border-gold/45 text-gold/95 bg-gold/10" },
  emerging: { label: "Emerging", className: "border-gold/30 text-gold/85 bg-gold/5" },
  development: { label: "In Development", className: "border-themed-muted/30 text-themed-muted bg-themed-muted/5" },
};

const PILLARS = [
  {
    icon: Radio,
    logo: null,
    name: "The SMILE Channel",
    role: "Author Visibility · Discovery Hub",
    desc: "Author visibility, discovery, promotional network, book amplification. The channel where aligned voices reach the people already searching for them.",
    href: SMILE_URL,
    cta: "Visit The SMILE Channel",
    status: "active",
  },
  {
    icon: Cpu,
    logo: "/partners/relictek-logo.webp",
    name: "RelicTek",
    role: "Technology Arm",
    desc: "Digital buildouts, platform development, automation, and AI-assisted infrastructure that gives the movement the technical backbone it needs.",
    href: RELICTEK_URL,
    cta: "Visit RelicTek",
    status: "active",
  },
  {
    icon: Hexagon,
    logo: "/partners/metatron-logo.webp",
    name: "Metatron Concepts",
    role: "Strategy · Systems · Concept Architecture",
    desc: "The strategy and concept layer. Systems architecture, brand and business intelligence, and the design of how movements scale.",
    href: METATRON_URL,
    cta: "Visit Metatron Concepts",
    status: "emerging",
  },
];

const EcosystemPage = () => {
  useEffect(() => {
    document.title = "Ecosystem · Authentic Momentum";
  }, []);
  return (
    <>
      <Navigation />
      <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />
        <section className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
              The Momentum Ecosystem
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl text-themed text-center leading-tight tracking-tight font-light">
              A movement{" "}
              <span className="italic gold-gradient-text">with infrastructure.</span>
            </h1>
            <p className="mt-8 text-themed-muted text-center max-w-2xl mx-auto leading-relaxed">
              Authentic Momentum does not sit alone. It is held by a growing
              network of platforms, partnerships, and systems designed to
              expand visibility, technology, strategy, and transformation.
            </p>
          </Reveal>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.name} delay={i * 120}>
                <div
                  data-testid={`ecosystem-page-card-${i}`}
                  className="glass-card p-8 h-full flex flex-col"
                >
                  {/* Header row: icon (left) and status badge (right)
                      live on the SAME flex row so they never collide
                      regardless of card width. Previously the badge
                      was absolute-positioned and overlapped the name. */}
                  <div className="flex items-start justify-between gap-3 mb-6">
                    {p.logo ? (
                      <div className="w-14 h-14 rounded-md bg-black/40 border border-gold/30 flex items-center justify-center overflow-hidden p-1.5 shrink-0">
                        <img
                          src={p.logo}
                          alt={`${p.name} logo`}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-14 h-14 border border-gold/40 flex items-center justify-center shrink-0">
                        <p.icon className="text-gold" size={22} strokeWidth={1.2} />
                      </div>
                    )}
                    {p.status ? (
                      <span
                        data-testid={`ecosystem-status-${p.status}`}
                        className={`text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[p.status].className}`}
                      >
                        {STATUS_STYLES[p.status].label}
                      </span>
                    ) : null}
                  </div>
                  <h3 className="font-serif text-2xl text-themed leading-tight">{p.name}</h3>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mt-3 mb-5">
                    {p.role}
                  </div>
                  <p className="text-themed-muted text-sm leading-relaxed flex-grow">{p.desc}</p>
                  {p.href ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`ecosystem-page-cta-link-${i}`}
                      className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold/85 hover:text-gold border-b border-gold/30 hover:border-gold pb-1 self-start transition-colors"
                    >
                      {p.cta} <ArrowUpRight size={12} />
                    </a>
                  ) : (
                    <div className="mt-6 inline-flex items-center text-[10px] tracking-[0.3em] uppercase text-gold/70 border border-gold/25 rounded-full px-4 py-2 self-start">
                      {p.cta}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={250}>
            <div className="mt-24 glass-card p-10">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <Layers className="text-gold" size={18} strokeWidth={1.2} />
                  <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80">
                    Future Ecosystem Layer
                  </div>
                </div>
                <span
                  data-testid="ecosystem-status-development"
                  className={`text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES.development.className}`}
                >
                  {STATUS_STYLES.development.label}
                </span>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl text-themed leading-tight font-light">
                Creative Capital Architecture.
              </h2>
              <p className="mt-4 text-themed-muted leading-relaxed text-sm">
                A future layer of the ecosystem focused on infrastructure that
                supports the long-term financial sovereignty of authentic
                creators and builders. Not yet active. Not financial advice.
                Disclosed here only as a roadmap signal so the movement can be
                read in full.
              </p>
            </div>
          </Reveal>

          <Reveal delay={350}>
            <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton
                data-testid="ecosystem-page-cta-blueprint"
                onClick={() => (window.location.href = "/?checkout=blueprint")}
                className="btn-gold"
              >
                Start Your Personal Action Plan
              </MagneticButton>
              <button
                type="button"
                data-testid="ecosystem-page-cta-orientation-reset"
                onClick={() => {
                  trackEvent("ecosystem_orientation_reset_cta_clicked");
                  window.dispatchEvent(
                    new CustomEvent("am:open-orientation-reset-checkout")
                  );
                }}
                className="btn-ghost-gold"
              >
                Book the Clarity Reset Session · $149
              </button>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default EcosystemPage;
